import { createRouter, createWebHashHistory, createWebHistory, START_LOCATION } from 'vue-router';
import { registerErrorRouter } from '@shared/composables/useErrorRouter';
import { APP_CONFIG } from '../config';
import { useUserStore } from '@shared/stores/user';
import { useOrganizationStore } from '@shared/stores/organization';
import { initApp } from '@shared/composables/useAppInit';
import { useReAuth } from '@shared/composables/useReAuth';
import { i18n, loadLocale } from '@shared/i18n';
import { scrollBehavior, trackScrollPositions } from '@shared/router-scroll';

// iOS WKWebView has issues with lazy-loaded chunks for this component - navigation silently
// fails unless the chunk is already loaded. Eager import works around the issue.
import NewsFormView from '@shared/views/news/NewsFormView.vue';

const isNative = !!window.kgrCapacitor;

const SUPPORTED_LOCALES = ['de', 'en', 'es', 'tr', 'uk'];

function detectLocale(): string {
    const current = i18n.global.locale.value;
    if (SUPPORTED_LOCALES.includes(current)) {
        return current;
    }
    const lang = navigator.language.slice(0, 2);
    return SUPPORTED_LOCALES.includes(lang) ? lang : 'de';
}

export const router = createRouter({
    history: isNative ? createWebHashHistory() : createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior,
    routes: [
        {
            path: '/',
            redirect: () => {
                const activeLocale = detectLocale();
                return useUserStore().isLoggedIn ? `/${activeLocale}/home/dashboard` : `/${activeLocale}/home/login`;
            },
        },
        {
            // catch paths without locale prefix and redirect — prevents Vue Router "No match" warnings
            path: '/:pathMatch(.*)*',
            redirect: (to) => {
                const segments = Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join('/') : to.params.pathMatch;
                // If the path already starts with a valid locale it is a genuine not-found under that
                // locale — re-prepending would loop forever (RangeError). Send it to the root instead.
                const firstSegment = segments.split('/')[0];
                if (SUPPORTED_LOCALES.includes(firstSegment)) {
                    return { path: '/', query: to.query, hash: to.hash };
                }
                const activeLocale = detectLocale();
                return { path: `/${activeLocale}/${segments}`, query: to.query, hash: to.hash };
            },
        },
        {
            path: '/:locale(de|en|es|tr|uk)',
            children: [
                {
                    path: '',
                    redirect: () => (useUserStore().isLoggedIn ? 'home/dashboard' : 'home/login'),
                },
                {
                    path: 'home',
                    children: [
                        {
                            path: '',
                            redirect: () => (useUserStore().isLoggedIn ? 'dashboard' : 'login'),
                        },
                        { path: 'login', component: () => import('../views/home/LoginView.vue') },
                        { path: 'dashboard', component: () => import('../views/home/DashboardView.vue'), meta: { requiresAuth: true } },
                    ],
                },
                {
                    path: 'account/:userId',
                    meta: { requiresAuth: true },
                    children: [
                        // invertedHeader on the profile only (Angular account-routing data); the
                        // edit + absences children keep the normal header.
                        {
                            path: '',
                            component: () => import('../views/account/ProfileView.vue'),
                            meta: { invertedHeader: true },
                        },
                        { path: 'edit', component: () => import('../views/account/EditProfileView.vue') },
                        // create + edit share the same form (Angular EditChildAbsenceComponent); the
                        // :absenceId variant pre-fills it from the absences list.
                        { path: 'absences/create', component: () => import('../views/account/EditChildAbsenceView.vue') },
                        { path: 'absences/:absenceId', component: () => import('../views/account/EditChildAbsenceView.vue') },
                    ],
                },
                {
                    path: 'news',
                    meta: { requiresAuth: true },
                    children: [
                        { path: '', component: () => import('@shared/views/news/NewsView.vue') },
                        { path: 'create', component: NewsFormView },
                        { path: ':id', component: () => import('@shared/views/news/NewsDetailView.vue') },
                        { path: ':id/edit', component: NewsFormView },
                    ],
                },
                {
                    path: 'events',
                    meta: { requiresAuth: true },
                    children: [
                        { path: '', component: () => import('@shared/views/events/EventsView.vue') },
                        { path: 'create', component: () => import('@shared/views/events/EventFormView.vue') },
                        { path: ':id', component: () => import('@shared/views/events/EventDetailView.vue') },
                        { path: ':id/edit', component: () => import('@shared/views/events/EventFormView.vue') },
                    ],
                },
                {
                    path: 'galleries',
                    meta: { requiresAuth: true },
                    children: [
                        { path: '', component: () => import('@shared/views/galleries/GalleriesView.vue') },
                        { path: ':id', component: () => import('@shared/views/galleries/GalleryDetailView.vue') },
                    ],
                },
                {
                    path: 'groups',
                    meta: { requiresAuth: true },
                    children: [
                        { path: '', component: () => import('@shared/views/groups/GroupsView.vue') },
                        { path: ':id', component: () => import('@shared/views/groups/GroupDetailView.vue') },
                    ],
                },
                {
                    path: 'chats',
                    meta: { requiresAuth: true },
                    children: [
                        { path: '', component: () => import('@shared/views/chats/ChatsView.vue') },
                        { path: ':id', component: () => import('@shared/views/chats/ChatDetailView.vue') },
                    ],
                },
                { path: 'diet', component: () => import('@shared/views/diet/DietView.vue'), meta: { requiresAuth: true } },
                {
                    path: 'files',
                    component: () => import('@shared/views/files/FilesView.vue'),
                    meta: { requiresAuth: true },
                    props: { appType: APP_CONFIG.appType, institutionType: APP_CONFIG.institutionType },
                },
                {
                    path: 'accounting',
                    component: () => import('@shared/views/accounting/InvoicesView.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'preferences',
                    meta: { requiresAuth: true },
                    children: [
                        {
                            path: '',
                            component: () => import('../views/preferences/PreferencesView.vue'),
                            children: [
                                { path: '', redirect: (to) => `/${to.params.locale as string}/preferences/notifications` },
                                { path: 'notifications', component: () => import('../views/preferences/NotificationsView.vue') },
                            ],
                        },
                    ],
                },
                {
                    path: 'organization',
                    component: () => import('@shared/views/organization/OrganizationView.vue'),
                    children: [
                        { path: '', redirect: (to) => `/${to.params.locale as string}/organization/contact` },
                        { path: 'contact', component: () => import('@shared/views/organization/ContactView.vue') },
                        { path: 'imprint', component: () => import('@shared/views/organization/ImprintView.vue') },
                    ],
                },
                {
                    // Deep-link/push target: legacy redirect route (customer-apps user-routing.module)
                    path: 'institution/:institutionId',
                    component: () => import('@shared/views/user/InstitutionRedirectView.vue'),
                },
                {
                    // Deep-link target of the app/universal links (federation-specific scheme)
                    path: 'federation/:federationId/institution/:institutionId/redirect',
                    component: () => import('@shared/views/user/InstitutionRedirectView.vue'),
                },
                {
                    // Kein Guard — 1:1 Angular `UserRoutingModule`. Der Kontowechsel muss gerade dann
                    // erreichbar sein, wenn die Session tot ist; ein `requiresAuth` wirft stattdessen
                    // auf den Login bzw. ins Re-Auth-Overlay. Sicher ist die Route ohne Guard, weil
                    // sie nur bereits auf dem Geraet gespeicherte Tokens anbietet, keine API ruft und
                    // `loginByToken()` den JWT vor dem Wechsel validiert.
                    path: 'user/switch',
                    component: () => import('@shared/views/user/SwitchUserView.vue'),
                },
                {
                    path: 'user/password/reset',
                    component: () => import('@shared/views/user/PasswordResetFlowView.vue'),
                },
                {
                    path: 'user/password/reset/:token',
                    component: () => import('@shared/views/user/PasswordResetFlowView.vue'),
                },
                {
                    // Angular ErrorRoutingModule (via app.config.ts). Target of redirectToError() when
                    // GET /users fails in initApp(); without it the catch-all bounced to the dashboard.
                    // The `**` wildcard is deliberately not ported: unknown paths keep redirecting home.
                    path: 'error',
                    name: 'error',
                    component: () => import('@shared/views/error/ErrorPageView.vue'),
                },
            ],
        },
    ],
});

// Angular injects `ErrorRouter` wherever it needs it; the shared helper needs the instance.
registerErrorRouter(router);

// Registered before the auth/locale guard so the outgoing offset is captured even when that guard
// redirects.
trackScrollPositions(router);

router.beforeEach(async (to, from) => {
    const locale = to.params.locale as string;

    await loadLocale(locale);

    const userStore = useUserStore();

    if (to.meta.requiresAuth && !userStore.isSessionValid) {
        // First load / deep-link → redirect to login (Angular AuthGuard: !router.navigated).
        if (from === START_LOCATION) {
            return { path: `/${locale}/home/login`, query: { redirect: to.fullPath } };
        }
        // In-app navigation with an expired/absent session → re-auth overlay (Angular layerLogin):
        // restore the session in place instead of bouncing to login. Cancel blocks the navigation.
        const success = await useReAuth().open();
        if (!success) {
            return false;
        }
    }

    // Not on the error page: initApp() is what sends the user there, and retrying it on arrival
    // would redirect again — an endless loop while offline.
    if (userStore.isLoggedIn && !useOrganizationStore().organization && to.name !== 'error') {
        await initApp();
    }
});
